import mapboxgl from "mapbox-gl";
import React, { useEffect, useRef } from "react";
import Map, { Layer, MapRef, Source } from "react-map-gl/mapbox";
import { globalAppConfig } from "../../config";
import Helper from "../../Helper";
import { MapObject } from "../../models/MapObject";
import { CommandMapLayer } from "../CommandTypes";

interface CommandCenterMapProps {
    project: any;
    data: any;
    map_objects: {
        id: number;
        project_id: number;
        mappable_type: string;
        mappable_id: number;
        uuid: string;
        geometry: "point" | "lineString" | "polygon";
        color: string;
        outline: string;
        opacity: number;
        icon: string;
        icon_anchor: string;
        icon_width: number;
        icon_units: string;
        coordinates: [number, number];
        created_at: string;
        updated_at: string;
        archived: boolean;
        radiuses: any[];
    }[];
    pixelRatio?: number;
}

const CommandCenterMap: React.FC<CommandCenterMapProps> = ({ project, data, map_objects, pixelRatio }) => {
    const mapRef = useRef<MapRef>(null);
    const [mapBounds, setMapBounds] = React.useState<mapboxgl.LngLatBounds>(new mapboxgl.LngLatBounds());
    const [loading, setLoading] = React.useState(true);

    const DEFAULT_MAX_ZOOM = 17; // Used for fitting the map bounds to the objects

    const mapObjects = React.useMemo(() => {
        return map_objects.map((obj) => MapObject.fromApiObject(obj, project.timezone));
    }, [map_objects]);

    const updateMetersPerPixel = () => {
        const map = mapRef?.current?.getMap();
        if (!map) {
            return;
        }

        const metersPerPixel = Helper.getMapMaxMetersPerPixelAtZoom(map);
        return metersPerPixel;
    };

    const mapMaxMeters = React.useMemo(() => {
        return updateMetersPerPixel();
    }, [mapRef]);

    const mapGeoJson = React.useMemo(() => {
        return Helper.generateMapGeoJson(mapObjects);
    }, [mapObjects]);

    const loadMapLayer = (object: MapObject) => {
        const map = mapRef?.current?.getMap();
        if (!map) {
            return null;
        }

        const iconSize = object.iconWidth
            ? Helper.getIconRealWorldSize(object, mapMaxMeters || 1)
            : Helper.defaultIconSizeArray(true);

        switch (object.geometry) {
            case "point":
                return Helper.addIcon(object, project.unique_token, map, "visible", iconSize);
            case "lineString":
                return Helper.addLine(object, project.unique_token, "visible", iconSize);
            case "polygon":
                return Helper.addPolygon(object, project.unique_token, "visible", iconSize);
            default:
                return null;
        }
    };

    const fitMapBounds = (retryCount: number = 0) => {
        if (project.map_zoom && project.lat_override && project.lon_override) {
            // mapView override in effect. Do not fit bounds
            return;
        }
        if (retryCount > 5) {
            return;
        }
        const map = mapRef?.current?.getMap();
        if (!map) {
            setTimeout(() => fitMapBounds(retryCount + 1), 500);
            return;
        }

        if (mapBounds.isEmpty()) {
            return;
        }

        map.fitBounds(mapBounds, {
            padding: 30,
            linear: true,
            maxZoom: DEFAULT_MAX_ZOOM,
        });
    };

    const onMapLoad = (retryCount: number = 0) => {
        setLoading(true);
        const map = mapRef?.current?.getMap();
        if (retryCount > 5) {
            setLoading(false);
            return;
        }
        if (!map) {
            setTimeout(() => onMapLoad(retryCount + 1), 500);
            return;
        }

        const necessaryIcons = [
            ...mapObjects
                .filter((obj): obj is MapObject & { icon: string } => Boolean(obj.icon))
                .map((obj) => obj.icon),
            "bullet_sdf_icon",
        ];

        const uniqueIcons = Array.from(new Set(necessaryIcons));

        Helper.addNecessaryMarkerImages(uniqueIcons, map).finally(() => setLoading(false));
    };

    useEffect(() => {
        const map = mapRef?.current?.getMap();
        if (loading || mapBounds.isEmpty() || !map) {
            return;
        }
        fitMapBounds();
    }, [mapBounds]);

    useEffect(() => {
        if (loading) {
            return;
        }
        const map = mapRef?.current?.getMap();
        if (!map || mapObjects.length === 0) {
            return;
        }

        let tempBounds = new mapboxgl.LngLatBounds();

        mapObjects.forEach((object) => {
            if (object.coordinates) {
                tempBounds = Helper.addToBounds(object, tempBounds);
            }
        });
        setMapBounds(tempBounds);
    }, [mapObjects, loading]);

    useEffect(() => {
        onMapLoad();
    }, [mapObjects, mapRef]);

    return (
        <div className="flex-1 min-h-0 w-full overflow-hidden">
            <Map
                interactive={false}
                reuseMaps={true}
                ref={mapRef}
                id={"commandMap"}
                initialViewState={{
                    // Use nullish coalescing so valid 0 coordinates/zoom are not treated as missing
                    longitude: project.lon_override ?? project.lon ?? -97.3539564,
                    latitude: project.lat_override ?? project.lat ?? 37.6876446,
                    zoom: project.map_zoom ?? 14,
                }}
                onLoad={() => onMapLoad()}
                mapStyle={project.map_style || "mapbox://styles/mapbox/satellite-v9"}
                mapboxAccessToken={globalAppConfig.MAP_BOX_ACCESS_TOKEN}
                logoPosition="bottom-left"
                style={{ width: "100%", height: "100%" }}
            >
                {data.geotiff && data.geotiff.status == "complete" && (
                    <Source
                        type="raster"
                        id={`${data.geotiff.id}_geotiff`}
                        url={`mapbox://nickdreiling.${data.geotiff.geotiff_name}`}
                    >
                        <Layer
                            id={`${data.geotiff.id}_geotiff`}
                            slot="middle"
                            type="raster"
                            source={`${data.geotiff.id}_geotiff`}
                        />
                    </Source>
                )}
                {data.map_layers
                    ?.filter((l: CommandMapLayer) => l.enabled)
                    .map((layer: CommandMapLayer) => (
                        <Source key={layer.id} type="raster" id={`layer_${layer.id}`} tiles={[layer.url]}>
                            <Layer id={`layer_${layer.id}`} slot="middle" type="raster" source={`layer_${layer.id}`} />
                        </Source>
                    ))}

                <Source type="geojson" id={project.unique_token} data={mapGeoJson}>
                    {!loading && mapObjects.map((mapObject) => loadMapLayer(mapObject))}
                </Source>
            </Map>
        </div>
    );
};

export default CommandCenterMap;
